import { ActionButton, ActionIconButton, ActionToggleGroup } from './ui/ActionButton';
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { isRtlLocale } from '../i18n/config';
import {
  DEFAULT_WORKOUT_BUILDER_SETTINGS,
  INTENSITY_METRIC_OPTIONS,
  LENGTH_METRIC_OPTIONS,
  type WorkoutBuilderSettings,
} from '../utils/workoutBuilderSettings';
import './workout-builder-setup.css';
import '../styles/workout-editor-system.css';

export interface WorkoutBuilderSetupModalProps {
  open: boolean;
  settings: WorkoutBuilderSettings;
  onChange: (settings: WorkoutBuilderSettings) => void;
  onConfirm: () => void;
  onCancel?: () => void;
  confirmed: boolean;
}

function ChipRow<T extends string>({ options, value, onSelect }: {
  options: { key: T; label: string }[];
  value: T;
  onSelect: (key: T) => void;
}) {
  return <div className="wb-setup__choices">
    {options.map(option => <ActionButton
      key={option.key}
      type="button"
      className={`wb-setup__choice${value === option.key ? ' is-selected' : ''}`}
      aria-pressed={value === option.key}
      onClick={() => onSelect(option.key)}
      wrap
    >{option.label}</ActionButton>)}
  </div>;
}

/** Step length and intensity basis. All new editor settings use target ranges. */
export function WorkoutBuilderSetupModal({ open, settings, onChange, onConfirm, onCancel, confirmed }: WorkoutBuilderSetupModalProps) {
  const { t, i18n } = useTranslation();
  const lengthOptions = useMemo(() => LENGTH_METRIC_OPTIONS.map(option => ({
    key: option.key, label: t(`workoutBuilder.setup.length.${option.key}`, option.label),
  })), [t]);
  const intensityOptions = useMemo(() => INTENSITY_METRIC_OPTIONS.map(option => ({
    key: option.key, label: t(`workoutBuilder.setup.intensity.${option.key}`, option.label),
  })), [t]);
  if (!open || typeof document === 'undefined') return null;
  const dismiss = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onCancel?.();
  };
  return createPortal(
    <div className="wb-setup-overlay" dir={isRtlLocale(i18n.language) ? 'rtl' : 'ltr'} onClick={onCancel ? dismiss : undefined}>
      <div className="title-bar-no-drag wb-setup" role="dialog" aria-modal="true" aria-labelledby="wb-setup-title" aria-describedby="wb-setup-description" onClick={event => event.stopPropagation()}>
        <header className="wb-setup__header">
          <h2 id="wb-setup-title">{confirmed ? t('workoutBuilder.setup.titleEdit') : t('workoutBuilder.setup.title')}</h2>
          {onCancel && <ActionIconButton
            type="button"
            className="wb-setup__close"
            onClick={dismiss}
            label={t('common.close')}
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" /></svg>}
          />}
        </header>
        <p id="wb-setup-description" className="wb-setup__description">{t('workoutBuilder.setup.subtitle')}</p>
        <fieldset className="wb-setup__group">
          <legend>{t('workoutBuilder.setup.stepLengthType')}</legend>
          <ChipRow options={lengthOptions} value={settings.primaryLengthMetric} onSelect={key => onChange({ ...settings, primaryLengthMetric: key, primaryIntensityTargetOrRange: 'range' })} />
        </fieldset>
        <fieldset className="wb-setup__group">
          <legend>{t('workoutBuilder.setup.intensityMetric')}</legend>
          <ChipRow options={intensityOptions} value={settings.primaryIntensityMetric} onSelect={key => onChange({ ...settings, primaryIntensityMetric: key, primaryIntensityTargetOrRange: 'range' })} />
        </fieldset>
        <footer className="wb-setup__actions">
          {onCancel && <ActionButton
            type="button"
            className="wb-setup__cancel"
            onClick={dismiss}
          >{t('workoutBuilder.cancel')}</ActionButton>}
          <ActionButton
            type="button"
            className="wb-setup__confirm"
            onClick={onConfirm}
          >{confirmed ? t('workoutBuilder.save') : t('workoutBuilder.setup.confirmContinue')}</ActionButton>
        </footer>
      </div>
    </div>, document.body,
  );
}
export { DEFAULT_WORKOUT_BUILDER_SETTINGS };
